import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAuth } from '@common/auth/use-auth';
import { useLogout } from '@common/auth/requests/logout';
import { useSettings } from '@ui/settings/use-settings';
import { useCustomMenu } from '@common/menus/use-custom-menu';
import { useThemeSelector } from '@ui/themes/theme-selector-context';

interface NavItem {
  label: string;
  action: string;
  target?: string;
}

export function IccdbNavbar() {
  const { user, isLoggedIn, hasPermission } = useAuth();
  const logout = useLogout();
  const { branding } = useSettings();
  const { selectedTheme, selectTheme } = useThemeSelector();
  const isDarkMode = selectedTheme?.is_dark ?? true;
  const customMenu = useCustomMenu('primary');
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Role detection
  const isDirector = hasPermission('director.access') || user?.roles?.some(r => r.name?.toLowerCase() === 'director');
  const isAdmin = hasPermission('admin.access') || hasPermission('admin') || user?.roles?.some(r => r.name?.toLowerCase() === 'admin' || r.name?.toLowerCase() === 'superadmin');
  const canAccessDirector = isDirector || isAdmin;
  const canAccessAdmin = isAdmin;

  // Close dropdown on outside click or escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setUserMenuOpen(false);
        setMobileMenuOpen(false);
      }
    };
    if (userMenuOpen || mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [userMenuOpen, mobileMenuOpen]);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Dynamic branding & logos adapted to current theme
  const logoLight = branding?.logo_light;
  const logoDark = branding?.logo_dark;
  const logoLightMobile = branding?.logo_light_mobile;
  const logoDarkMobile = branding?.logo_dark_mobile;

  // In dark mode: prefer light logo; In light mode: prefer dark logo
  const desktopLogo = isDarkMode ? (logoLight || logoDark) : (logoDark || logoLight);
  const mobileLogo = isDarkMode
    ? (logoLightMobile || logoDarkMobile || desktopLogo)
    : (logoDarkMobile || logoLightMobile || desktopLogo);
  const siteName = branding?.site_name || 'ICCDB';
  const siteDescription = branding?.site_description;

  // Theme-adaptive colors
  const navBg = isDarkMode ? '#0f172a' : '#ffffff';
  const navBorder = isDarkMode ? '#1e293b' : '#e2e8f0';
  const navText = isDarkMode ? '#f8fafc' : '#0f172a';
  const linkColor = isDarkMode ? '#cbd5e1' : '#475569';
  const searchBg = isDarkMode ? '#1e293b' : '#f1f5f9';
  const searchBorder = isDarkMode ? '#334155' : '#cbd5e1';
  const searchColor = isDarkMode ? '#f8fafc' : '#0f172a';

  // Dynamic navigation items from customMenu or default discovery links
  const menuItems: NavItem[] = customMenu?.items && customMenu.items.length > 0
    ? customMenu.items.map(item => ({
      label: item.label,
      action: item.action,
      target: item.target,
    }))
    : [
      { label: 'Movies', action: '/movies' },
      { label: 'Series', action: '/series' },
      { label: 'People', action: '/people' },
      { label: 'News', action: '/news' },
    ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?query=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const toggleTheme = () => {
    selectTheme(isDarkMode ? 'light' : 'dark');
  };

  // Safe user profile slug and URL
  const userSlug = (user?.first_name || user?.name || 'profile')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-');
  const profileUrl = user?.id ? `/user/${user.id}/${userSlug}` : '#';

  const userInitial = user?.first_name
    ? user.first_name[0].toUpperCase()
    : user?.name
      ? user.name[0].toUpperCase()
      : user?.email
        ? user.email[0].toUpperCase()
        : 'U';

  const userDisplayName = user?.first_name
    ? `${user.first_name} ${user.last_name || ''}`.trim()
    : (user?.name || user?.email?.split('@')[0] || 'My Account');

  return (
    <header
      className="sticky top-0 z-50 shadow-md transition-colors duration-200"
      style={{
        backgroundColor: navBg,
        borderBottom: `1px solid ${navBorder}`,
        width: '100%',
        minHeight: '64px',
        height: '64px',
        display: 'flex',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 50,
        color: navText,
        boxSizing: 'border-box',
        overflowX: 'clip',
      }}
    >
      <style>{`
        @media (max-width: 768px) {
          .iccdb-nav-desktop-links {
            display: none !important;
          }
          .iccdb-nav-desktop-search {
            display: none !important;
          }
          .iccdb-nav-desktop-actions {
            display: none !important;
          }
          .iccdb-nav-mobile-trigger {
            display: flex !important;
          }
        }
        @media (min-width: 769px) {
          .iccdb-nav-desktop-links {
            display: flex !important;
          }
          .iccdb-nav-desktop-search {
            display: flex !important;
          }
          .iccdb-nav-desktop-actions {
            display: flex !important;
          }
          .iccdb-nav-mobile-trigger {
            display: none !important;
          }
        }
      `}</style>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          maxWidth: '1380px',
          margin: '0 auto',
          padding: '0 clamp(12px, 3vw, 24px)',
          height: '100%',
          gap: '12px',
          boxSizing: 'border-box',
        }}
      >
        {/* Dynamic Logo */}
        {desktopLogo ? (
          <Link
            to="/"
            style={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              flexShrink: 0,
              height: '42px',
            }}
            aria-label={siteName}
          >
            <picture style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              {mobileLogo && (
                <source srcSet={mobileLogo} media="(max-width: 768px)" />
              )}
              <source srcSet={desktopLogo} media="(min-width: 768px)" />
              <img
                src={desktopLogo}
                alt={siteName}
                style={{
                  height: '34px',
                  maxHeight: '36px',
                  width: 'auto',
                  maxWidth: '160px',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </picture>
          </Link>
        ) : (
          <Link
            to="/"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              textDecoration: 'none',
              flexShrink: 0,
            }}
            aria-label={siteName}
          >
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #f59e0b, #e11d48)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 900,
                color: '#ffffff',
                fontSize: '16px',
                boxShadow: '0 4px 14px rgba(245, 158, 11, 0.35)',
              }}
            >
              {siteName.slice(0, 2).toUpperCase()}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span
                style={{
                  fontWeight: 900,
                  fontSize: '19px',
                  letterSpacing: '-0.5px',
                  lineHeight: '1.1',
                  color: navText,
                }}
              >
                {siteName}
              </span>
              {siteDescription && (
                <span
                  style={{
                    fontSize: '9px',
                    fontWeight: 700,
                    letterSpacing: '2px',
                    textTransform: 'uppercase',
                    color: '#f59e0b',
                    lineHeight: '1',
                    marginTop: '1px',
                  }}
                >
                  {siteDescription}
                </span>
              )}
            </div>
          </Link>
        )}

        {/* Dynamic Desktop Navigation Links */}
        <nav
          className="iccdb-nav-desktop-links"
          style={{
            alignItems: 'center',
            gap: '8px',
            margin: '0 12px',
          }}
        >
          {menuItems.map((item, idx) => (
            <Link
              key={item.action + idx}
              to={item.action}
              target={item.target}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                color: linkColor,
                textDecoration: 'none',
                transition: 'all 0.2s',
              }}
              className="hover:text-amber-500 transition-colors"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Search Bar */}
        <form
          onSubmit={handleSearch}
          className="iccdb-nav-desktop-search"
          style={{
            position: 'relative',
            alignItems: 'center',
            flex: 1,
            maxWidth: '300px',
            margin: '0 8px',
          }}
        >
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search movies, actors, actresses..."
            style={{
              width: '100%',
              height: '36px',
              backgroundColor: searchBg,
              color: searchColor,
              fontSize: '13px',
              borderRadius: '9999px',
              paddingLeft: '36px',
              paddingRight: '14px',
              border: `1px solid ${searchBorder}`,
              outline: 'none',
              transition: 'background-color 0.2s, border-color 0.2s',
            }}
          />
          <svg
            style={{
              width: '16px',
              height: '16px',
              color: '#94a3b8',
              position: 'absolute',
              left: '12px',
              pointerEvents: 'none',
            }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </form>

        {/* Right Actions: Theme Toggle + User Menu (Desktop only) */}
        <div
          className="iccdb-nav-desktop-actions"
          style={{
            alignItems: 'center',
            gap: '12px',
            flexShrink: 0,
          }}
        >
          {/* Light / Dark Mode Switcher */}
          <button
            type="button"
            onClick={toggleTheme}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '9999px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isDarkMode ? '#1e293b' : '#f1f5f9',
              border: isDarkMode ? '1px solid #334155' : '1px solid #cbd5e1',
              color: isDarkMode ? '#fbbf24' : '#475569',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            className="hover:scale-105 transition-transform"
            aria-label={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {isDarkMode ? (
              <svg style={{ width: '17px', height: '17px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg style={{ width: '17px', height: '17px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>

          {/* User Icon & Dropdown Menu */}
          {isLoggedIn ? (
            <div
              ref={userMenuRef}
              style={{
                position: 'relative',
                display: 'inline-block',
              }}
            >
              {/* Trigger Button with User Avatar & Subtle Arrow */}
              <button
                type="button"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                aria-expanded={userMenuOpen}
                aria-haspopup="true"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '3px 8px 3px 4px',
                  borderRadius: '9999px',
                  backgroundColor: userMenuOpen
                    ? (isDarkMode ? '#1e293b' : '#e2e8f0')
                    : (isDarkMode ? 'rgba(30, 41, 59, 0.7)' : 'rgba(241, 245, 249, 0.9)'),
                  border: userMenuOpen
                    ? '1.5px solid #f59e0b'
                    : `1px solid ${isDarkMode ? '#334155' : '#cbd5e1'}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  outline: 'none',
                }}
                className="hover:border-amber-500/80 transition-all"
                title={userDisplayName}
              >
                {/* User Avatar Circle */}
                {user?.image ? (
                  <img
                    src={user.image}
                    alt={userDisplayName}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '9999px',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '9999px',
                      background: 'linear-gradient(135deg, #f59e0b 0%, #e11d48 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: '13.5px',
                      color: '#ffffff',
                      boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
                    }}
                  >
                    {userInitial}
                  </div>
                )}

                {/* Dropdown Chevron */}
                <svg
                  style={{
                    width: '13px',
                    height: '13px',
                    color: isDarkMode ? '#94a3b8' : '#64748b',
                    transform: userMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* User Dropdown Menu */}
              {userMenuOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 10px)',
                    right: 0,
                    width: '295px',
                    backgroundColor: isDarkMode ? '#0b1120' : '#ffffff',
                    border: `1px solid ${isDarkMode ? '#1e293b' : '#e2e8f0'}`,
                    borderRadius: '20px',
                    boxShadow: isDarkMode
                      ? '0 20px 45px -10px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06)'
                      : '0 20px 45px -10px rgba(0,0,0,0.14), 0 0 0 1px rgba(0,0,0,0.04)',
                    padding: '12px',
                    zIndex: 100,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}
                >
                  {/* 1. User Info Header */}
                  <div
                    style={{
                      padding: '10px 12px 14px',
                      borderBottom: `1px solid ${isDarkMode ? '#1e293b' : '#f1f5f9'}`,
                      marginBottom: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    {/* Large Avatar */}
                    {user?.image ? (
                      <img
                        src={user.image}
                        alt={userDisplayName}
                        style={{
                          width: '42px',
                          height: '42px',
                          borderRadius: '9999px',
                          objectFit: 'cover',
                          border: '2px solid #f59e0b',
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '42px',
                          height: '42px',
                          borderRadius: '9999px',
                          background: 'linear-gradient(135deg, #f59e0b 0%, #e11d48 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 900,
                          fontSize: '18px',
                          color: '#ffffff',
                          boxShadow: '0 4px 12px rgba(245, 158, 11, 0.35)',
                          flexShrink: 0,
                        }}
                      >
                        {userInitial}
                      </div>
                    )}

                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                      <span
                        style={{
                          fontSize: '14.5px',
                          fontWeight: 800,
                          color: isDarkMode ? '#f8fafc' : '#0f172a',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {userDisplayName}
                      </span>
                      <span
                        style={{
                          fontSize: '12px',
                          color: isDarkMode ? '#94a3b8' : '#64748b',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          marginTop: '1px',
                        }}
                      >
                        {user?.email}
                      </span>

                      {/* Dynamic Role Badges */}
                      <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {isAdmin && (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '2px 8px',
                              borderRadius: '9999px',
                              fontSize: '10px',
                              fontWeight: 800,
                              backgroundColor: 'rgba(99, 102, 241, 0.15)',
                              color: isDarkMode ? '#a5b4fc' : '#4f46e5',
                              border: '1px solid rgba(99, 102, 241, 0.3)',
                            }}
                          >
                            <i className="fa-solid fa-shield-halved" style={{ fontSize: '10px' }}></i> Administrator
                          </span>
                        )}
                        {isDirector && (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '2px 8px',
                              borderRadius: '9999px',
                              fontSize: '10px',
                              fontWeight: 800,
                              backgroundColor: 'rgba(245, 158, 11, 0.15)',
                              color: '#f59e0b',
                              border: '1px solid rgba(245, 158, 11, 0.3)',
                            }}
                          >
                            <i className="fa-solid fa-clapperboard" style={{ fontSize: '10px' }}></i> Director
                          </span>
                        )}
                        {!isAdmin && !isDirector && (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '2px 8px',
                              borderRadius: '9999px',
                              fontSize: '10px',
                              fontWeight: 800,
                              backgroundColor: isDarkMode ? 'rgba(148, 163, 184, 0.12)' : 'rgba(100, 116, 139, 0.12)',
                              color: isDarkMode ? '#cbd5e1' : '#475569',
                              border: `1px solid ${isDarkMode ? 'rgba(148, 163, 184, 0.25)' : 'rgba(100, 116, 139, 0.2)'}`,
                            }}
                          >
                            <i className="fa-solid fa-star" style={{ fontSize: '9px', color: '#f59e0b' }}></i> Cinema Member
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 2. Menu Links */}

                  {/* Item: Profile */}
                  <Link
                    to={profileUrl}
                    onClick={() => setUserMenuOpen(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '9px 12px',
                      borderRadius: '12px',
                      textDecoration: 'none',
                      color: isDarkMode ? '#e2e8f0' : '#1e293b',
                      transition: 'all 0.15s ease',
                    }}
                    className={isDarkMode ? 'hover:bg-slate-800/80 hover:text-amber-400' : 'hover:bg-slate-100 hover:text-amber-600'}
                  >
                    <div
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '8px',
                        backgroundColor: isDarkMode ? 'rgba(56, 189, 248, 0.15)' : 'rgba(56, 189, 248, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#38bdf8',
                        flexShrink: 0,
                      }}
                    >
                      <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700 }}>Profile</span>
                      <span style={{ fontSize: '11px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>View ratings, reviews & activity</span>
                    </div>
                  </Link>

                  {/* Item: Director Portal (Conditional: if director or admin) */}
                  {canAccessDirector && (
                    <Link
                      to="/director"
                      onClick={() => setUserMenuOpen(false)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '9px 12px',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        color: '#f59e0b',
                        backgroundColor: isDarkMode ? 'rgba(245, 158, 11, 0.08)' : 'rgba(245, 158, 11, 0.06)',
                        border: '1px solid rgba(245, 158, 11, 0.25)',
                        transition: 'all 0.15s ease',
                      }}
                      className={isDarkMode ? 'hover:bg-amber-500/20' : 'hover:bg-amber-500/12'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '8px',
                            backgroundColor: 'rgba(245, 158, 11, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#f59e0b',
                            flexShrink: 0,
                          }}
                        >
                          <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontSize: '13px', fontWeight: 800 }}>Director Portal</span>
                          <span style={{ fontSize: '11px', color: isDarkMode ? '#fbbf24' : '#d97706', opacity: 0.9 }}>
                            Submit movies & track approvals
                          </span>
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: '10px',
                          fontWeight: 800,
                          padding: '2px 6px',
                          borderRadius: '6px',
                          backgroundColor: 'rgba(245, 158, 11, 0.25)',
                          color: '#f59e0b',
                        }}
                      >
                        Director
                      </span>
                    </Link>
                  )}

                  {/* Item: Visit Admin (Conditional: if admin) */}
                  {canAccessAdmin && (
                    <Link
                      to="/admin"
                      onClick={() => setUserMenuOpen(false)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '9px 12px',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        color: isDarkMode ? '#a5b4fc' : '#4f46e5',
                        backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.06)',
                        border: '1px solid rgba(99, 102, 241, 0.25)',
                        transition: 'all 0.15s ease',
                      }}
                      className={isDarkMode ? 'hover:bg-indigo-500/20' : 'hover:bg-indigo-500/12'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '8px',
                            backgroundColor: 'rgba(99, 102, 241, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: isDarkMode ? '#a5b4fc' : '#4f46e5',
                            flexShrink: 0,
                          }}
                        >
                          <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          </svg>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontSize: '13px', fontWeight: 800 }}>Visit Admin</span>
                          <span style={{ fontSize: '11px', color: isDarkMode ? '#c7d2fe' : '#6366f1', opacity: 0.9 }}>
                            System settings & management
                          </span>
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: '10px',
                          fontWeight: 800,
                          padding: '2px 6px',
                          borderRadius: '6px',
                          backgroundColor: 'rgba(99, 102, 241, 0.25)',
                          color: isDarkMode ? '#a5b4fc' : '#4f46e5',
                        }}
                      >
                        Admin
                      </span>
                    </Link>
                  )}

                  {/* Item: Watchlist & Lists */}
                  <Link
                    to={`${profileUrl}/lists`}
                    onClick={() => setUserMenuOpen(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '9px 12px',
                      borderRadius: '12px',
                      textDecoration: 'none',
                      color: isDarkMode ? '#e2e8f0' : '#1e293b',
                      transition: 'all 0.15s ease',
                    }}
                    className={isDarkMode ? 'hover:bg-slate-800/80 hover:text-amber-400' : 'hover:bg-slate-100 hover:text-amber-600'}
                  >
                    <div
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '8px',
                        backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#10b981',
                        flexShrink: 0,
                      }}
                    >
                      <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                      </svg>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700 }}>Watchlist & Lists</span>
                      <span style={{ fontSize: '11px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>Saved titles & bookmarks</span>
                    </div>
                  </Link>

                  {/* Item: Account Settings */}
                  <Link
                    to="/account-settings"
                    onClick={() => setUserMenuOpen(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '9px 12px',
                      borderRadius: '12px',
                      textDecoration: 'none',
                      color: isDarkMode ? '#e2e8f0' : '#1e293b',
                      transition: 'all 0.15s ease',
                    }}
                    className={isDarkMode ? 'hover:bg-slate-800/80 hover:text-amber-400' : 'hover:bg-slate-100 hover:text-amber-600'}
                  >
                    <div
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '8px',
                        backgroundColor: isDarkMode ? 'rgba(148, 163, 184, 0.12)' : 'rgba(148, 163, 184, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#94a3b8',
                        flexShrink: 0,
                      }}
                    >
                      <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                      </svg>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700 }}>Account Settings</span>
                      <span style={{ fontSize: '11px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>Security, email & password</span>
                    </div>
                  </Link>

                  {/* Divider */}
                  <div style={{ height: '1px', backgroundColor: isDarkMode ? '#1e293b' : '#f1f5f9', margin: '4px 0' }} />

                  {/* Item: Sign Out */}
                  <button
                    type="button"
                    onClick={() => {
                      setUserMenuOpen(false);
                      logout.mutate();
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '9px 12px',
                      borderRadius: '12px',
                      border: 'none',
                      backgroundColor: 'transparent',
                      color: '#f43f5e',
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'left',
                      transition: 'all 0.15s ease',
                    }}
                    className="hover:bg-rose-500/10 hover:text-rose-400"
                  >
                    <div
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '8px',
                        backgroundColor: 'rgba(244, 63, 94, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#f43f5e',
                        flexShrink: 0,
                      }}
                    >
                      <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700 }}>Sign Out</span>
                      <span style={{ fontSize: '11px', color: isDarkMode ? '#fda4af' : '#fb7185', opacity: 0.8 }}>
                        End current session
                      </span>
                    </div>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Link
                to="/login"
                style={{
                  padding: '6px 14px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: linkColor,
                  textDecoration: 'none',
                  borderRadius: '8px',
                  transition: 'all 0.15s ease',
                }}
                className="hover:text-amber-500"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                style={{
                  padding: '7px 18px',
                  borderRadius: '9999px',
                  fontSize: '13px',
                  fontWeight: 700,
                  background: 'linear-gradient(90deg, #f59e0b, #e11d48)',
                  color: '#ffffff',
                  textDecoration: 'none',
                  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                }}
              >
                Join
              </Link>
            </div>
          )}
        </div>

        {/* Mobile Menu Hamburger Trigger (Mobile Only) */}
        <button
          type="button"
          onClick={() => setMobileMenuOpen(true)}
          className="iccdb-nav-mobile-trigger"
          style={{
            width: '38px',
            height: '38px',
            borderRadius: '10px',
            color: isDarkMode ? '#f8fafc' : '#0f172a',
            backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.7)' : 'rgba(241, 245, 249, 0.9)',
            border: `1px solid ${isDarkMode ? '#334155' : '#cbd5e1'}`,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
          aria-label="Open mobile navigation drawer"
        >
          <i className="fa-solid fa-bars" style={{ fontSize: '18px' }}></i>
        </button>
      </div>

      {/* Mobile Navigation Drawer Backdrop */}
      {mobileMenuOpen && (
        <div
          onClick={() => setMobileMenuOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            zIndex: 99,
          }}
          aria-hidden="true"
        />
      )}

      {/* Mobile Slide-in Navigation Drawer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(330px, 86vw)',
          height: '100dvh',
          backgroundColor: navBg,
          borderLeft: `1px solid ${navBorder}`,
          boxShadow: '-10px 0 35px rgba(0, 0, 0, 0.5)',
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          pointerEvents: mobileMenuOpen ? 'auto' : 'none',
          visibility: mobileMenuOpen ? 'visible' : 'hidden',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile Navigation"
      >
        {/* Drawer Header with Logo & Close Button */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: `1px solid ${navBorder}`,
            minHeight: '64px',
          }}
        >
          {desktopLogo ? (
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
            >
              <img
                src={desktopLogo}
                alt={siteName}
                style={{ height: '30px', width: 'auto', maxWidth: '140px', objectFit: 'contain' }}
              />
            </Link>
          ) : (
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #f59e0b, #e11d48)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 900,
                  color: '#ffffff',
                  fontSize: '15px',
                }}
              >
                {siteName.slice(0, 2).toUpperCase()}
              </div>
              <span style={{ fontWeight: 900, fontSize: '18px', color: navText }}>
                {siteName}
              </span>
            </Link>
          )}

          <button
            type="button"
            onClick={() => setMobileMenuOpen(false)}
            style={{
              width: '34px',
              height: '34px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isDarkMode ? '#1e293b' : '#f1f5f9',
              border: `1px solid ${isDarkMode ? '#334155' : '#cbd5e1'}`,
              color: isDarkMode ? '#cbd5e1' : '#475569',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            aria-label="Close drawer"
          >
            <i className="fa-solid fa-xmark" style={{ fontSize: '16px' }}></i>
          </button>
        </div>

        {/* Drawer Body (Scrollable) */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px 18px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}
        >
          {/* Quick Search Form */}
          <form
            onSubmit={(e) => {
              handleSearch(e);
              setMobileMenuOpen(false);
            }}
            style={{ position: 'relative', width: '100%' }}
          >
            <i
              className="fa-solid fa-magnifying-glass"
              style={{
                position: 'absolute',
                left: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#f59e0b',
                fontSize: '13px',
                pointerEvents: 'none',
              }}
            ></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search movies, talent..."
              style={{
                width: '100%',
                height: '40px',
                backgroundColor: searchBg,
                color: searchColor,
                border: `1px solid ${searchBorder}`,
                borderRadius: '12px',
                paddingLeft: '38px',
                paddingRight: '12px',
                fontSize: '13px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </form>

          {/* 1. Discovery Catalog Links */}
          <div>
            <div
              style={{
                fontSize: '11px',
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.07em',
                color: isDarkMode ? '#64748b' : '#94a3b8',
                marginBottom: '10px',
                paddingLeft: '4px',
              }}
            >
              Discovery Catalog
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Link
                to="/movies"
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  color: navText,
                  fontSize: '14px',
                  fontWeight: 600,
                  transition: 'background-color 0.15s',
                }}
                className={isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}
              >
                <div
                  style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(245, 158, 11, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#f59e0b',
                    fontSize: '13px',
                  }}
                >
                  <i className="fa-solid fa-film"></i>
                </div>
                <span>All Movies</span>
              </Link>

              <Link
                to="/series"
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  color: navText,
                  fontSize: '14px',
                  fontWeight: 600,
                  transition: 'background-color 0.15s',
                }}
                className={isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}
              >
                <div
                  style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(6, 182, 212, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#06b6d4',
                    fontSize: '13px',
                  }}
                >
                  <i className="fa-solid fa-tv"></i>
                </div>
                <span>TV & Web Series</span>
              </Link>

              <Link
                to="/people"
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  color: navText,
                  fontSize: '14px',
                  fontWeight: 600,
                  transition: 'background-color 0.15s',
                }}
                className={isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}
              >
                <div
                  style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(99, 102, 241, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6366f1',
                    fontSize: '13px',
                  }}
                >
                  <i className="fa-solid fa-users"></i>
                </div>
                <span>Stars & Personalities</span>
              </Link>

              <Link
                to="/actors"
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  color: navText,
                  fontSize: '14px',
                  fontWeight: 600,
                  transition: 'background-color 0.15s',
                }}
                className={isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}
              >
                <div
                  style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(37, 99, 235, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#2563eb',
                    fontSize: '13px',
                  }}
                >
                  <i className="fa-solid fa-user-tie"></i>
                </div>
                <span>Actors</span>
              </Link>

              <Link
                to="/actresses"
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  color: navText,
                  fontSize: '14px',
                  fontWeight: 600,
                  transition: 'background-color 0.15s',
                }}
                className={isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}
              >
                <div
                  style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(236, 72, 153, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ec4899',
                    fontSize: '13px',
                  }}
                >
                  <i className="fa-solid fa-venus"></i>
                </div>
                <span>Actresses</span>
              </Link>

              <Link
                to="/news"
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  color: navText,
                  fontSize: '14px',
                  fontWeight: 600,
                  transition: 'background-color 0.15s',
                }}
                className={isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}
              >
                <div
                  style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(16, 185, 129, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#10b981',
                    fontSize: '13px',
                  }}
                >
                  <i className="fa-solid fa-newspaper"></i>
                </div>
                <span>Cinema News</span>
              </Link>
            </div>
          </div>

          {/* 2. User Account & Portals */}
          <div style={{ paddingTop: '8px', borderTop: `1px solid ${navBorder}` }}>
            <div
              style={{
                fontSize: '11px',
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.07em',
                color: isDarkMode ? '#64748b' : '#94a3b8',
                marginBottom: '10px',
                paddingLeft: '4px',
              }}
            >
              Account & Portals
            </div>

            {isLoggedIn ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {/* Profile Identity Card */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 12px',
                    borderRadius: '12px',
                    backgroundColor: isDarkMode ? '#1e293b' : '#f1f5f9',
                    border: `1px solid ${isDarkMode ? '#334155' : '#cbd5e1'}`,
                    marginBottom: '8px',
                  }}
                >
                  {user?.image ? (
                    <img
                      src={user.image}
                      alt={userDisplayName}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #f59e0b, #e11d48)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 900,
                        fontSize: '14px',
                        color: '#ffffff',
                      }}
                    >
                      {userInitial}
                    </div>
                  )}

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '13.5px',
                        fontWeight: 800,
                        color: navText,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {userDisplayName}
                    </div>
                    <div
                      style={{
                        fontSize: '11px',
                        color: linkColor,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {user?.email}
                    </div>
                  </div>
                </div>

                {/* Profile */}
                <Link
                  to={profileUrl}
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '9px 12px',
                    borderRadius: '10px',
                    textDecoration: 'none',
                    color: navText,
                    fontSize: '13.5px',
                    fontWeight: 600,
                  }}
                  className={isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}
                >
                  <i className="fa-solid fa-user" style={{ width: '18px', color: '#38bdf8' }}></i>
                  <span>Profile</span>
                </Link>

                {/* Director Portal (Conditional) */}
                {canAccessDirector && (
                  <Link
                    to="/director"
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '9px 12px',
                      borderRadius: '10px',
                      textDecoration: 'none',
                      color: '#f59e0b',
                      backgroundColor: 'rgba(245, 158, 11, 0.1)',
                      border: '1px solid rgba(245, 158, 11, 0.25)',
                      fontSize: '13.5px',
                      fontWeight: 700,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <i className="fa-solid fa-clapperboard" style={{ width: '18px', color: '#f59e0b' }}></i>
                      <span>Director Portal</span>
                    </div>
                    <span
                      style={{
                        fontSize: '10px',
                        fontWeight: 800,
                        padding: '1px 6px',
                        borderRadius: '4px',
                        backgroundColor: 'rgba(245, 158, 11, 0.2)',
                        color: '#f59e0b',
                      }}
                    >
                      Portal
                    </span>
                  </Link>
                )}

                {/* Visit Admin (Conditional) */}
                {canAccessAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '9px 12px',
                      borderRadius: '10px',
                      textDecoration: 'none',
                      color: isDarkMode ? '#a5b4fc' : '#4f46e5',
                      backgroundColor: 'rgba(99, 102, 241, 0.1)',
                      border: '1px solid rgba(99, 102, 241, 0.25)',
                      fontSize: '13.5px',
                      fontWeight: 700,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <i className="fa-solid fa-shield-halved" style={{ width: '18px', color: '#818cf8' }}></i>
                      <span>Visit Admin</span>
                    </div>
                    <span
                      style={{
                        fontSize: '10px',
                        fontWeight: 800,
                        padding: '1px 6px',
                        borderRadius: '4px',
                        backgroundColor: 'rgba(99, 102, 241, 0.2)',
                        color: isDarkMode ? '#a5b4fc' : '#4f46e5',
                      }}
                    >
                      Admin
                    </span>
                  </Link>
                )}

                {/* Watchlist & Lists */}
                <Link
                  to={`${profileUrl}/lists`}
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '9px 12px',
                    borderRadius: '10px',
                    textDecoration: 'none',
                    color: navText,
                    fontSize: '13.5px',
                    fontWeight: 600,
                  }}
                  className={isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}
                >
                  <i className="fa-solid fa-bookmark" style={{ width: '18px', color: '#10b981' }}></i>
                  <span>Watchlist & Lists</span>
                </Link>

                {/* Account Settings */}
                <Link
                  to="/account-settings"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '9px 12px',
                    borderRadius: '10px',
                    textDecoration: 'none',
                    color: navText,
                    fontSize: '13.5px',
                    fontWeight: 600,
                  }}
                  className={isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}
                >
                  <i className="fa-solid fa-gear" style={{ width: '18px', color: '#94a3b8' }}></i>
                  <span>Account Settings</span>
                </Link>

                {/* Sign Out */}
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    logout.mutate();
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '9px 12px',
                    borderRadius: '10px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: '#f43f5e',
                    fontSize: '13.5px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                  }}
                  className="hover:bg-rose-500/10"
                >
                  <i className="fa-solid fa-arrow-right-from-bracket" style={{ width: '18px', color: '#f43f5e' }}></i>
                  <span>Sign Out</span>
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '10px',
                    borderRadius: '10px',
                    border: `1px solid ${navBorder}`,
                    fontSize: '13.5px',
                    fontWeight: 700,
                    color: navText,
                    textDecoration: 'none',
                    backgroundColor: isDarkMode ? '#1e293b' : '#f8fafc',
                  }}
                >
                  <i className="fa-solid fa-right-to-bracket"></i>
                  <span>Sign In</span>
                </Link>

                <Link
                  to="/register"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '10px',
                    borderRadius: '10px',
                    background: 'linear-gradient(90deg, #f59e0b, #e11d48)',
                    fontSize: '13.5px',
                    fontWeight: 700,
                    color: '#ffffff',
                    textDecoration: 'none',
                    boxShadow: '0 4px 14px rgba(245, 158, 11, 0.35)',
                  }}
                >
                  <i className="fa-solid fa-user-plus"></i>
                  <span>Join ICCDB</span>
                </Link>
              </div>
            )}
          </div>

          {/* 3. Appearance Switcher (Inside Drawer) */}
          <div style={{ paddingTop: '8px', borderTop: `1px solid ${navBorder}` }}>
            <div
              style={{
                fontSize: '11px',
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.07em',
                color: isDarkMode ? '#64748b' : '#94a3b8',
                marginBottom: '10px',
                paddingLeft: '4px',
              }}
            >
              Preferences
            </div>

            <button
              type="button"
              onClick={toggleTheme}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 12px',
                borderRadius: '10px',
                backgroundColor: isDarkMode ? '#1e293b' : '#f1f5f9',
                border: `1px solid ${isDarkMode ? '#334155' : '#cbd5e1'}`,
                color: navText,
                fontSize: '13.5px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i
                  className={isDarkMode ? 'fa-solid fa-moon text-amber-400' : 'fa-solid fa-sun text-amber-500'}
                  style={{ width: '18px' }}
                ></i>
                <span>{isDarkMode ? 'Dark Theme' : 'Light Theme'}</span>
              </div>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: linkColor,
                }}
              >
                Switch
              </span>
            </button>
          </div>
        </div>

        {/* Drawer Bottom Footnote */}
        <div
          style={{
            padding: '14px 20px',
            borderTop: `1px solid ${navBorder}`,
            fontSize: '11px',
            color: isDarkMode ? '#64748b' : '#94a3b8',
            textAlign: 'center',
          }}
        >
          {siteName} Cinema Database • Mobile Experience
        </div>
      </div>
    </header>
  );
}
